/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AfterViewInit, Component } from '@angular/core';
import { DashboardDataModel, ScheduleModel, TierModel, WidgetModel } from '../../../../models';
import { DashboardService } from '../../../../services/component';
import { PortalRbacService } from '../../../../services/utils';
import { UtilsService } from '../../../../../../../core/services';
import { WidgetComponent } from '../widgetBaseComponent/widget.component';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-schedules-widget',
    templateUrl: '../widgetBaseComponent/widget.component.html',
    styleUrls: ['../widgetBaseComponent/widget.component.scss']
})
export class SchedulesWidgetComponent extends WidgetComponent implements AfterViewInit {
    constructor(
        dashboardService: DashboardService,
        private portalRbacService: PortalRbacService,
        utilsService: UtilsService
    ) {
        super(dashboardService, utilsService);

        const data: DashboardDataModel = this.dashboardService.allData;
        this.setUpPage(data);
    }

    ngAfterViewInit(): void {
        this.dashboardService.allDataEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe(
                (data: any): void => {
                    this.setUpPage(data);
                }
            );
    }

    /**
     * Set up page.
     * @param data
     * @private
     */
    private setUpPage(data: any): void {
        const scheduleDetails: any = this.formatScheduleData(data.schedules);
        this.view = this.getViewData(data.schedules, scheduleDetails);
        this.loaded = true;
    }

    /**
     * Format schedule data.
     * @private
     * @param schedulesList
     */
    private formatScheduleData(schedulesList: ScheduleModel[] = []): any {
        const scheduleDetails = { schedules: 0, next: '' };
        const now = new Date();
        const datesAfterNow: ScheduleModel[] = schedulesList.filter((item: ScheduleModel): string => {
            if (item.scheduleTime > now.toISOString()) {
                return item.scheduleTime;
            }
        });

        if (schedulesList.length) {
            scheduleDetails.schedules = schedulesList.length;
        }

        if (datesAfterNow.length) {
            const sortedByDate: any = this.utilsService.sortArrayByProperty(datesAfterNow, 'scheduleTime');
            scheduleDetails.next = this.utilsService.createDateTimeString(sortedByDate[0].scheduleTime);
        }

        return scheduleDetails;
    }

    /**
     * Get schedule view data.
     * @param schedulesList
     * @param scheduleDetails
     * @private
     */
    private getViewData(schedulesList: any[], scheduleDetails: { schedules: any; next: any; }): WidgetModel {
        const selectedTier: TierModel = this.portalRbacService.selectedTier;
        let viewObject: WidgetModel = this.getBaseMessaging(this.baseView);

        const error: any = this.errors?.schedules;
        if (error) {
            return this.getErrorMessaging(viewObject, error);
        }

        if (schedulesList.length) {
            viewObject = this.getActiveMessaging(viewObject, scheduleDetails);
        }

        if (selectedTier.tier === 'free') {
            viewObject = this.getFreeTierMessaging(viewObject);
        }

        return viewObject;
    }

    /**
     * Get base view messaging.
     * @param {WidgetModel} baseView
     * @returns {WidgetModel}
     * @private
     */
    private getBaseMessaging(baseView: WidgetModel): WidgetModel {
        const baseData = {
            iconSrc: 'assets/centralisedManagement/icons/schedule.svg',
            title: 'scheduler',
            flagState: 'success',
            subtitle: 'Schedule a back up or update',
            message: 'Did you know you can schedule a back up or update for your ADCs?',
            linkText: 'Create a schedule',
            linkUrl: '/cm/scheduler/schedules/new',
        };

        return this.updateObject(baseView, baseData);
    }

    /**
     * Get Free Tier view messaging.
     * @param {WidgetModel} baseView
     * @returns {WidgetModel}
     * @private
     */
    private getFreeTierMessaging(baseView: WidgetModel): WidgetModel {
        const baseData = {
            iconSrc: 'assets/centralisedManagement/icons/schedule.svg',
            title: 'scheduler',
            flagState: 'upgrade',
            mainImageSrc: 'assets/centralisedManagement/empty-state/schedules.svg',
            subtitle: 'Schedule a back up or update',
            message: 'Set a convenient time to update and back up your appliance, with 1 year of history.',
            linkText: 'More info',
            linkUrl: '/cm/scheduler/schedules',
        };

        return this.updateObject(baseView, baseData);
    }

    /**
     * Get active messaging.
     * @param viewObject
     * @param scheduleDetails
     * @private
     */
    private getActiveMessaging(viewObject: WidgetModel, scheduleDetails: { schedules: any; next?: any; }): WidgetModel {
        const newData = {
            flagState: 'hidden',
            subtitle: null,
            message: `Next schedule ${scheduleDetails.next}`,
            linkText: 'View my Schedules',
            linkUrl: '/cm/scheduler/schedules',
            counters: [
                {
                    count: `${scheduleDetails.schedules}`,
                    countSubTitle: scheduleDetails.schedules === 1 ? 'Schedule' : 'Schedules',
                    subTitleClass: 'success'
                }
            ]
        };

        if (!scheduleDetails.next) {
            newData.message = 'You have no future schedules.';
        }

        return this.updateObject(viewObject, newData);
    }
}
